import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { countries } from '../utils/countries';
import './Settings.css';

const API = (path) =>
    process.env.NODE_ENV === 'development'
        ? `http://localhost:5001/api${path}`
        : `/api${path}`;

const Settings = ({ onBack, currentUser, isSuperUser = false, companySlug = "", companyName = "" }) => {
    const [activeTab, setActiveTab] = useState('global');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    // ─── Global Settings ───────────────────────────────────────────────────────
    const [globalSettings, setGlobalSettings] = useState({
        fuelSurcharge: '0.3075',
        conversionRate: '280.50',
        upsClientId: '',
        upsClientSecret: '',
        upsShipperNumber: '',
        peakChargesUSD: '0',
        peakChargesFromDate: '',
        peakChargesToDate: '',
        peakChargesSheet: [], // Array of { weight, USA, ROW }
        peakChargesFileName: '', // To show which file is loaded
        specialSurcharges: [] // Array of { country, amountUSD, fromDate, toDate }
    });

    // ─── Tariff Upload ─────────────────────────────────────────────────────────
    const [tariffData, setTariffData] = useState({
        account: '',
        product: 'NON-DOCUMENTS',
        serviceType: 'Export Saver',
        fdCharges: '21.50',
        rawData: '',
        accountComments: {}
    });
    const [zoneRawData, setZoneRawData] = useState('');
    const [zoneCount, setZoneCount] = useState(0);
    const [existingTariffs, setExistingTariffs] = useState([]);
    const [isProductBoxSheet, setIsProductBoxSheet] = useState(false);

    // ─── Import Tariff Upload ──────────────────────────────────────────────────
    const [importTariffData, setImportTariffData] = useState({
        account: '',
        product: 'NON-DOCUMENTS',
        serviceType: 'Import Saver',
        rawData: ''
    });
    const [existingImportTariffs, setExistingImportTariffs] = useState([]);

    // ─── Special Surcharges Form ───────────────────────────────────────────────
    const [specialSurchargeForm, setSpecialSurchargeForm] = useState({
        country: '',
        amountUSD: '',
        fromDate: '',
        toDate: ''
    });

    // ─── Receipt Reset Modal ───────────────────────────────────────────────────
    const [showReceiptResetModal, setShowReceiptResetModal] = useState(false);
    const [receiptResetStep, setReceiptResetStep] = useState(1);
    const [receiptResetNumber, setReceiptResetNumber] = useState('');
    const [receiptResetPin, setReceiptResetPin] = useState('');
    const [receiptResetLoading, setReceiptResetLoading] = useState(false);

    useEffect(() => {
        fetchSettings();
        fetchTariffs();
        fetchZones();
        fetchImportTariffs();
    }, [companySlug]);

    const showMsg = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 4000);
    };

    // ─── Fetch helpers ─────────────────────────────────────────────────────────
    const fetchZones = async () => {
        try {
            const res = await axios.get(API('/settings/zones/count'));
            if (res.data.success) setZoneCount(res.data.count);
        } catch (err) { console.error('Failed to fetch zones'); }
    };

    const fetchTariffs = async () => {
        try {
            const endpoint = companySlug
                ? API(`/companies/${companySlug}/tariffs`)
                : API('/settings/tariffs');
            const res = await axios.get(endpoint);
            if (res.data.success) setExistingTariffs(res.data.tariffs);
        } catch (err) { console.error('Failed to fetch tariffs'); }
    };

    const fetchImportTariffs = async () => {
        try {
            const res = await axios.get(API('/settings/import-tariffs'));
            if (res.data.success) setExistingImportTariffs(res.data.tariffs);
        } catch (err) { console.error('Failed to fetch import tariffs'); }
    };

    const fetchSettings = async () => {
        try {
            const res = await axios.get(API('/settings'));
            if (res.data.success && res.data.settings) {
                const s = res.data.settings;
                console.log('[Settings] ===== FETCH DEBUG START =====');
                console.log('[Settings] Response from server:', JSON.stringify(res.data, null, 2));
                console.log('[Settings] s.specialSurcharges from server:', s.specialSurcharges);
                console.log('[Settings] s.specialSurcharges type:', typeof s.specialSurcharges);
                console.log('[Settings] s.specialSurcharges is array?:', Array.isArray(s.specialSurcharges));
                console.log('[Settings] ===== FETCH DEBUG END =====');

                setGlobalSettings({
                    fuelSurcharge: (parseFloat(s.fuelSurcharge || 0) * 100).toFixed(2),
                    conversionRate: s.conversionRate || '280.50',
                    upsClientId: s.upsClientId || '',
                    upsClientSecret: s.upsClientSecret || '',
                    upsShipperNumber: s.upsShipperNumber || '',
                    peakChargesUSD: s.peakChargesUSD || '0',
                    peakChargesFromDate: s.peakChargesFromDate || '',
                    peakChargesToDate: s.peakChargesToDate || '',
                    peakChargesSheet: s.peakChargesSheet || [],
                    peakChargesFileName: s.peakChargesFileName || '',
                    specialSurcharges: s.specialSurcharges || []
                });
                console.log('[Settings] State updated with specialSurcharges:', (s.specialSurcharges || []));
                console.log('[Settings] State updated with peakChargesSheet:', (s.peakChargesSheet || []).length, 'rows');
            }
        } catch (err) { console.error('Failed to fetch settings:', err); }
    };

    // ─── Global Settings Handlers ──────────────────────────────────────────────
    const saveGlobalSettings = async (sourceSettings = null, auto = false) => {
        const settingsToSave = sourceSettings || globalSettings;
        setLoading(true);
        try {
            const payload = {
                fuelSurcharge: (parseFloat(settingsToSave.fuelSurcharge) / 100).toString(),
                conversionRate: settingsToSave.conversionRate,
                upsClientId: settingsToSave.upsClientId,
                upsClientSecret: settingsToSave.upsClientSecret,
                upsShipperNumber: settingsToSave.upsShipperNumber,
                peakChargesUSD: settingsToSave.peakChargesUSD,
                peakChargesFromDate: settingsToSave.peakChargesFromDate,
                peakChargesToDate: settingsToSave.peakChargesToDate,
                peakChargesSheet: settingsToSave.peakChargesSheet || [],
                peakChargesFileName: settingsToSave.peakChargesFileName || '',
                specialSurcharges: settingsToSave.specialSurcharges || []
            };

            console.log('[Settings] ===== EXPLICIT PAYLOAD DEBUG =====');
            console.log('[Settings] settingsToSave:', settingsToSave);
            console.log('[Settings] Payload to send:', JSON.stringify(payload, null, 2));
            console.log('[Settings] Is specialSurcharges an array?:', Array.isArray(payload.specialSurcharges));
            console.log('[Settings] specialSurcharges length:', (payload.specialSurcharges || []).length);
            console.log('[Settings] ===== END PAYLOAD DEBUG =====');

            const res = await axios.post(API('/settings'), payload, {
                headers: { 'Content-Type': 'application/json' }
            });
            console.log('[Settings] ✅ Response from server:', res.data);

            if (res.data.success) {
                if (!auto) {
                    showMsg('✅ Global settings updated — changes apply to ALL companies');
                } else {
                    showMsg('✅ Surcharges saved to database');
                }
                // Always refresh local settings from server after update
                await fetchSettings();
            }
        } catch (err) {
            console.error('[Settings] Save error:', err);
            showMsg('Failed to save settings: ' + (err.response?.data?.message || err.message), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveGlobal = async () => {
        await saveGlobalSettings();
    };

    // ─── Special Surcharges Handlers ────────────────────────────────────────────
    const handleAddSpecialSurcharge = async () => {
        if (!specialSurchargeForm.country || !specialSurchargeForm.amountUSD || !specialSurchargeForm.fromDate || !specialSurchargeForm.toDate) {
            showMsg('❌ Please fill all special surcharge fields', 'error');
            return;
        }

        // Validate that fromDate <= toDate
        const fromDate = new Date(specialSurchargeForm.fromDate);
        const toDate = new Date(specialSurchargeForm.toDate);
        if (fromDate > toDate) {
            showMsg('❌ Valid From Date must be before or equal to Valid To Date', 'error');
            return;
        }

        // Warn if date range doesn't include today (helps catch accidental single-day ranges)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (today < fromDate || today > toDate) {
            showMsg(
                `⚠️ This surcharge is currently INACTIVE (valid ${specialSurchargeForm.fromDate} to ${specialSurchargeForm.toDate}). ` +
                `To activate now, set a range that includes today (${today.toISOString().split('T')[0]}).`,
                'warning'
            );
        }

        if (fromDate.getTime() === toDate.getTime()) {
            showMsg('ℹ️ You are adding a single-day surcharge. Confirm this is intended.', 'info');
        }

        const newSurcharge = {
            country: specialSurchargeForm.country.toUpperCase(),
            amountUSD: parseFloat(specialSurchargeForm.amountUSD).toFixed(2),
            fromDate: specialSurchargeForm.fromDate,
            toDate: specialSurchargeForm.toDate
        };

        const nextSpecialSurcharges = [...(globalSettings.specialSurcharges || []), newSurcharge];
        const nextGlobalSettings = { ...globalSettings, specialSurcharges: nextSpecialSurcharges };

        setGlobalSettings(nextGlobalSettings);
        setSpecialSurchargeForm({ country: '', amountUSD: '', fromDate: '', toDate: '' });

        await saveGlobalSettings(nextGlobalSettings, true);
    };

    const handleRemoveSpecialSurcharge = async (index) => {
        const nextSpecialSurcharges = (globalSettings.specialSurcharges || []).filter((_, i) => i !== index);
        const nextGlobalSettings = { ...globalSettings, specialSurcharges: nextSpecialSurcharges };

        setGlobalSettings(nextGlobalSettings);
        await saveGlobalSettings(nextGlobalSettings, true);
    };

    const handleVerifySpecialSurcharges = async () => {
        try {
            const res = await axios.get(API('/settings/special-surcharges-diagnostic'));
            if (res.data.success) {
                const surcharges = res.data.specialSurcharges || [];
                console.log('[Settings] Diagnostic result:', res.data);
                showMsg(`✅ Database Status: ${surcharges.length} surcharge(s) stored in database`, 'success');
                if (surcharges.length === 0) {
                    console.warn('[Settings] Zero surcharges in database. Have you clicked "Update Settings"?');
                }
            }
        } catch (err) {
            console.error('[Settings] Verification error:', err);
            showMsg('Failed to verify database state', 'error');
        }
    };

    const handleTestSaveSurcharge = async () => {
        try {
            console.log('[Settings] TEST: Sending test surcharge save...');
            const testPayload = {
                testSurcharges: [
                    {
                        country: 'TEST',
                        amountUSD: '99.99',
                        fromDate: '2026-01-01',
                        toDate: '2026-12-31'
                    }
                ]
            };
            console.log('[Settings] TEST: Payload:', testPayload);

            const res = await axios.post(API('/settings/test-save-surcharge'), testPayload);
            console.log('[Settings] TEST: Response:', res.data);
            showMsg('✅ Test save completed! Check server logs.', 'success');
        } catch (err) {
            console.error('[Settings] TEST Save error:', err);
            showMsg('Test save failed: ' + err.message, 'error');
        }
    };

    const handleClearTokenCache = async () => {
        try {
            await axios.post(API('/settings/clear-ups-cache'));
            showMsg('UPS Token cache cleared. New credentials will take effect on next call.');
        } catch (err) { showMsg('Failed to clear cache', 'error'); }
    };

    // ─── Tariff Handlers ───────────────────────────────────────────────────────
    const handleExcelTariff = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const XLSX = require('xlsx');
                const wb = XLSX.read(evt.target.result, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
                if (data.length < 2) throw new Error('Sheet is empty');
                const headers = data[0].map(h => String(h || '').trim());
                const weightIdx = headers.findIndex(h => h.toLowerCase() === 'weight');
                const accountIdx = headers.findIndex(h => h.toLowerCase() === 'account');
                if (weightIdx === -1) throw new Error('Mandatory "Weight" column not found');
                if (accountIdx !== -1 && data[1][accountIdx]) {
                    setTariffData(prev => ({ ...prev, account: String(data[1][accountIdx]) }));
                }
                const rawText = data.map(row => row.join('\t')).join('\n');
                setTariffData(prev => ({ ...prev, rawData: rawText }));
                showMsg(`Excel parsed: ${data.length - 1} rows found. Click Save to upload.`);
            } catch (err) {
                showMsg(`Excel Error: ${err.message}`, 'error');
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleExcelPeakCharges = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const fileName = file.name;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const XLSX = require('xlsx');
                const wb = XLSX.read(evt.target.result, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
                if (data.length < 2) throw new Error('Sheet is empty');

                const headers = data[0].map(h => String(h || '').trim().toLowerCase());
                console.log('[Settings] Parsed headers:', headers);

                const weightIdx = headers.findIndex(h => h.includes('weight'));
                const usaIdx = headers.findIndex(h => h.includes('usa'));
                const rowIdx = headers.findIndex(h => h.includes('row'));

                if (weightIdx === -1 || usaIdx === -1 || rowIdx === -1) {
                    throw new Error(`Mandatory columns not found. Found headers: ${headers.join(', ')}. Expected: Weight, USA, ROW`);
                }

                const sheet = [];
                for (let i = 1; i < data.length; i++) {
                    const row = data[i];
                    if (row[weightIdx] === undefined || row[weightIdx] === null || String(row[weightIdx]).trim() === '') continue;

                    const weightText = String(row[weightIdx]).replace(/[^\d.]/g, '');
                    const weight = parseFloat(weightText);
                    if (isNaN(weight)) continue;

                    const cleanVal = (val) => {
                        if (val === undefined || val === null) return 0;
                        const s = String(val).replace(/[^\d.]/g, '');
                        return parseFloat(s) || 0;
                    };

                    sheet.push({
                        weight: weight,
                        USA: cleanVal(row[usaIdx]),
                        ROW: cleanVal(row[rowIdx])
                    });
                }

                if (sheet.length === 0) throw new Error('No valid data rows found after parsing.');

                setGlobalSettings(prev => ({ ...prev, peakChargesSheet: sheet, peakChargesFileName: fileName }));
                showMsg(`✅ Peak Charges Sheet parsed: ${sheet.length} rows. Remember to click "Update Settings" to save.`);
            } catch (err) {
                console.error('[Settings] Peak Sheet Parse Error:', err);
                showMsg(`Peak Charges Error: ${err.message}`, 'error');
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleDownloadPeakTemplate = () => {
        try {
            const XLSX = require('xlsx');
            const data = [
                ['Weight', 'USA', 'ROW'],
                [0.5, 2.50, 2.95],
                [1.0, 2.50, 2.95],
                [1.5, 3.75, 4.43],
                [2.0, 5.00, 5.90]
            ];
            const ws = XLSX.utils.aoa_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Peak_Charges");
            XLSX.writeFile(wb, "Peak_Charges_Template.xlsx");
            showMsg('Template downloaded!');
        } catch (err) {
            showMsg('Failed to generate template', 'error');
        }
    };

    const handleExcelZones = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const XLSX = require('xlsx');
                const wb = XLSX.read(evt.target.result, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
                const validRows = data.filter(row => row.length >= 3 && String(row[0]).length === 2);
                if (validRows.length === 0) throw new Error('No valid Code/Country/Zone rows found.');
                const rawText = validRows.map(row => row.slice(0, 3).join('\t')).join('\n');
                setZoneRawData(rawText);
                showMsg(`Parsed ${validRows.length} zones. Click Update to save.`);
            } catch (err) {
                showMsg(`Zone Excel Error: ${err.message}`, 'error');
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleUploadTariff = async () => {
        if (!tariffData.account || !tariffData.rawData) {
            showMsg('Account and Data are required (Upload Excel first)', 'error');
            return;
        }
        setLoading(true);
        try {
            const endpoint = companySlug
                ? API(`/companies/${companySlug}/tariffs/upload`)
                : API('/settings/upload-tariff');
            const res = await axios.post(endpoint, tariffData);
            showMsg(`Success! Saved ${res.data.count} weight tiers for ${tariffData.account}`);
            setTariffData({ account: '', product: 'NON-DOCUMENTS', serviceType: 'Export Saver', fdCharges: '21.50', rawData: '', accountComments: {} });
            fetchTariffs();
        } catch (err) {
            showMsg(err.response?.data?.message || 'Upload failed', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUploadZones = async () => {
        if (!zoneRawData) {
            showMsg('Please upload a zone excel file first', 'error');
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(API('/settings/upload-zones'), { rawData: zoneRawData });
            showMsg(`Zone list updated: ${res.data.count} countries mapped`);
            setZoneRawData('');
            fetchZones();
        } catch (err) {
            showMsg('Failed to update zones', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSheet = async (account, serviceType) => {
        if (!window.confirm(`Delete sheet for ${account} (${serviceType})?`)) return;
        try {
            const endpoint = companySlug
                ? API(`/companies/${companySlug}/tariffs/${account}/${serviceType}`)
                : API(`/settings/tariffs/${account}/${serviceType}`);
            await axios.delete(endpoint);
            showMsg(`Deleted sheet for ${account}`);
            fetchTariffs();
        } catch (err) {
            showMsg('Failed to delete sheet', 'error');
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const res = await axios.get(API('/settings/download-tariff-template'), { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Tariff_Template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            showMsg('Template downloaded! Fill it out and upload.');
        } catch (err) {
            showMsg('Failed to download template', 'error');
        }
    };

    const handleDownloadProductBoxTemplate = async () => {
        try {
            const res = await axios.get(API('/settings/download-product-box-template'), { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Product_Box_Template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            showMsg('Product Box template downloaded!');
        } catch (err) {
            showMsg('Failed to download template', 'error');
        }
    };

    // ─── Receipt Reset Modal Handlers ──────────────────────────────────────────
    const handleOpenReceiptResetModal = () => {
        setShowReceiptResetModal(true);
        setReceiptResetStep(1);
        setReceiptResetNumber('');
        setReceiptResetPin('');
    };

    const handleCloseReceiptResetModal = () => {
        setShowReceiptResetModal(false);
        setReceiptResetStep(1);
        setReceiptResetNumber('');
        setReceiptResetPin('');
    };

    const handleSendReceiptResetCode = async () => {
        if (!receiptResetNumber) {
            showMsg('Please enter a receipt number', 'error');
            return;
        }
        try {
            setReceiptResetLoading(true);
            const res = await axios.post(API('/settings/receipt/reset-request'), {
                startValue: receiptResetNumber,
                email: currentUser
            });
            if (res.data.success) {
                setReceiptResetStep(2);
                showMsg('Verification code sent to your email.');
            }
        } catch (err) {
            showMsg(err.response?.data?.message || 'Failed to send verification code', 'error');
        } finally {
            setReceiptResetLoading(false);
        }
    };

    const handleConfirmReceiptReset = async () => {
        if (!receiptResetPin) {
            showMsg('Please enter the verification code', 'error');
            return;
        }
        try {
            setReceiptResetLoading(true);
            const res = await axios.post(API('/settings/receipt/reset-confirm'), { pin: receiptResetPin });
            if (res.data.success) {
                showMsg(res.data.message || `Receipt number successfully reset to ${receiptResetNumber}`);
                handleCloseReceiptResetModal();
            }
        } catch (err) {
            showMsg(err.response?.data?.message || 'Failed to verify code', 'error');
        } finally {
            setReceiptResetLoading(false);
        }
    };

    // ─── Styles ────────────────────────────────────────────────────────────────
    const card = {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
        padding: '2rem',
        marginBottom: '1.5rem',
    };

    const sectionTitle = (icon, label) => (
        <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            fontSize: '1.05rem', fontWeight: 800, color: '#2d3748',
            marginBottom: '1.25rem', paddingBottom: '0.75rem',
            borderBottom: '2px solid #f0f4f8'
        }}>
            <span style={{ fontSize: '1.25rem' }}>{icon}</span> {label}
        </div>
    );

    const inputSt = {
        width: '100%', padding: '0.7rem 0.9rem', borderRadius: '8px',
        border: '1.5px solid #e2e8f0', fontSize: '0.95rem',
        background: '#f8fafc', boxSizing: 'border-box', outline: 'none',
    };

    const labelSt = {
        display: 'block', fontWeight: 600, fontSize: '0.82rem',
        color: '#4a5568', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.5px'
    };

    // ─── Import Tariff Handlers ────────────────────────────────────────────────
    const handleExcelImportTariff = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const XLSX = require('xlsx');
                const wb = XLSX.read(evt.target.result, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
                if (data.length < 2) throw new Error('Sheet is empty');
                const headers = data[0].map(h => String(h || '').trim());
                const weightIdx = headers.findIndex(h => h.toLowerCase() === 'weight');
                const accountIdx = headers.findIndex(h => h.toLowerCase() === 'account');
                if (weightIdx === -1) throw new Error('Mandatory "Weight" column not found');
                if (accountIdx !== -1 && data[1][accountIdx]) {
                    setImportTariffData(prev => ({ ...prev, account: String(data[1][accountIdx]) }));
                }
                const rawText = data.map(row => row.join('\t')).join('\n');
                setImportTariffData(prev => ({ ...prev, rawData: rawText }));
                showMsg(`Import Excel parsed: ${data.length - 1} rows. Click Save to upload.`);
            } catch (err) {
                showMsg(`Import Excel Error: ${err.message}`, 'error');
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleUploadImportTariff = async () => {
        if (!importTariffData.account || !importTariffData.rawData) {
            showMsg('Account and Data are required (Upload Excel first)', 'error');
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(API('/settings/upload-import-tariff'), importTariffData);
            showMsg(`✅ Saved ${res.data.count} weight tiers for import account ${importTariffData.account}`);
            setImportTariffData({ account: '', product: 'NON-DOCUMENTS', serviceType: 'Import Saver', rawData: '' });
            fetchImportTariffs();
        } catch (err) {
            showMsg(err.response?.data?.message || 'Upload failed', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteImportSheet = async (account, serviceType) => {
        if (!window.confirm(`Delete import sheet for ${account} (${serviceType})?`)) return;
        try {
            await axios.delete(API(`/settings/import-tariffs/${account}/${serviceType}`));
            showMsg(`Deleted import sheet for ${account}`);
            fetchImportTariffs();
        } catch (err) {
            showMsg('Failed to delete import sheet', 'error');
        }
    };

    const handleDownloadImportTemplate = async () => {
        try {
            const res = await axios.get(API('/settings/download-import-tariff-template'), { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Import_Tariff_Template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            showMsg('Import template downloaded!');
        } catch (err) {
            showMsg('Failed to download import template', 'error');
        }
    };

    const tabs = [
        { id: 'global', label: '🌍 Global Settings', desc: 'Rates, Peak Charges, Zones' },
        { id: 'tariffs', label: '📦 Freight Tariffs', desc: 'Upload & manage rate sheets' },
        { id: 'import-tariffs', label: '✈️ Import Tariffs', desc: 'FC Label import rate sheets' },
        { id: 'receipts', label: '🧾 Receipt Management', desc: 'Invoice counter & management' },
    ];

    // ─── Render ────────────────────────────────────────────────────────────────
    return (
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto', fontFamily: "'Inter', sans-serif" }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem', gap: '1rem' }}>
                <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#4a5568' }}>←</button>
                <div>
                    <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800, color: '#1a202c' }}>⚙️ System Settings</h2>
                    <p style={{ margin: '4px 0 0', color: '#718096', fontSize: '0.88rem' }}>
                        {companyName
                            ? `Company: ${companyName} — ${companySlug ? 'Configure isolated company settings' : 'Configure global rates and settings'}`
                            : 'Configure global rates, freight tariffs, and receipt management'
                        }
                    </p>
                    {!isSuperUser && companySlug && (
                        <p style={{ margin: '8px 0 0', color: '#d69e2e', fontSize: '0.8rem', fontWeight: 600 }}>
                            ℹ️ You can only manage this company's tariffs and receipts
                        </p>
                    )}
                    {!isSuperUser && !companySlug && (
                        <p style={{ margin: '8px 0 0', color: '#c53030', fontSize: '0.8rem', fontWeight: 600 }}>
                            ⚠️ Global settings are restricted to super users only
                        </p>
                    )}
                </div>
                {message && (
                    <div style={{
                        marginLeft: 'auto', padding: '0.6rem 1.2rem', borderRadius: '10px',
                        background: message.type === 'success' ? '#c6f6d5' : '#fed7d7',
                        color: message.type === 'success' ? '#276749' : '#c53030',
                        fontWeight: 600, fontSize: '0.88rem'
                    }}>
                        {message.text}
                    </div>
                )}
            </div>

            {/* Tab Bar - Filter based on permissions */}
            <div style={{
                display: 'flex', gap: '0.5rem', marginBottom: '2rem',
                background: 'white', padding: '6px', borderRadius: '14px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.07)', width: 'fit-content'
            }}>
                {tabs
                    .filter(tab => {
                        // Only super users can access global settings
                        if (tab.id === 'global' && !isSuperUser) return false;
                        // Company users can access tariffs and receipts
                        return true;
                    })
                    .map(tab => (
                        <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                            style={{
                                padding: '0.65rem 1.4rem', borderRadius: '10px', border: 'none',
                                cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', transition: 'all 0.2s',
                                background: activeTab === tab.id
                                    ? 'linear-gradient(135deg, #667eea, #764ba2)'
                                    : 'transparent',
                                color: activeTab === tab.id ? 'white' : '#718096',
                                boxShadow: activeTab === tab.id ? '0 4px 12px rgba(102,126,234,0.3)' : 'none'
                            }}>
                            {tab.label}
                        </button>
                    ))}
            </div>

            {/* ══════════════════════════════════════════════════ */}
            {/* TAB: GLOBAL SETTINGS                              */}
            {/* ══════════════════════════════════════════════════ */}
            {activeTab === 'global' && (
                isSuperUser ? (
                    <div>
                        {/* Notice Banner */}
                        <div style={{
                            background: 'linear-gradient(135deg, #667eea15, #764ba215)',
                            border: '1px solid #667eea40', borderRadius: '14px',
                            padding: '1.2rem 1.5rem', marginBottom: '1.5rem',
                            display: 'flex', alignItems: 'center', gap: '12px'
                        }}>
                            <span style={{ fontSize: '1.5rem' }}>🌍</span>
                            <div>
                                <div style={{ fontWeight: 800, color: '#553c9a', fontSize: '0.95rem' }}>Global Configuration</div>
                                <div style={{ color: '#744792', fontSize: '0.83rem', marginTop: '2px' }}>
                                    These settings apply to <strong>ALL companies</strong>. Changes here will immediately affect all shipment calculations across the entire platform.
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

                            {/* Left: Currency & UPS Config */}
                            <div style={card}>
                                {sectionTitle('💱', 'Currency & UPS Config')}

                                <div style={{ marginBottom: '1.2rem' }}>
                                    <label style={labelSt}>Fuel Surcharge Rate (%)</label>
                                    <input style={inputSt} type="number" step="0.0001"
                                        value={globalSettings.fuelSurcharge}
                                        onChange={e => setGlobalSettings(p => ({ ...p, fuelSurcharge: e.target.value }))} />
                                    <p style={{ fontSize: '0.75rem', color: '#a0aec0', margin: '4px 0 0' }}>
                                        Applied to (Freight + UPS Surcharges + Peak/Country Surcharges). Current: {globalSettings.fuelSurcharge}%
                                    </p>
                                </div>

                                <div style={{ marginBottom: '1.2rem' }}>
                                    <label style={labelSt}>USD to PKR Conversion Rate</label>
                                    <input style={inputSt} type="number"
                                        value={globalSettings.conversionRate}
                                        onChange={e => setGlobalSettings(p => ({ ...p, conversionRate: e.target.value }))} />
                                    <p style={{ fontSize: '0.75rem', color: '#a0aec0', margin: '4px 0 0' }}>
                                        1 USD = {globalSettings.conversionRate} PKR
                                    </p>
                                </div>

                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={labelSt}>UPS Shipper Number (Env Default)</label>
                                    <input style={inputSt} type="text"
                                        value={globalSettings.upsShipperNumber}
                                        onChange={e => setGlobalSettings(p => ({ ...p, upsShipperNumber: e.target.value }))}
                                        placeholder="e.g. 499A1A" />
                                </div>

                                <div style={{ display: 'flex', gap: '0.75rem' }}>
                                    <button className="settings-btn" onClick={handleSaveGlobal} disabled={loading}
                                        style={{ flex: 1, background: 'linear-gradient(135deg, #667eea, #764ba2)', marginBottom: 0 }}>
                                        {loading ? '⏳ Saving...' : '💾 Update Settings'}
                                    </button>
                                    <button className="settings-btn" onClick={handleClearTokenCache}
                                        style={{ background: '#718096', marginBottom: 0 }}>
                                        🔄 Clear UPS Cache
                                    </button>
                                </div>
                            </div>

                            {/* Right: Peak Period Charges */}
                            <div style={card}>
                                {sectionTitle('📈', 'Peak Period Charges')}

                                <div style={{
                                    background: '#fffbf0', border: '1px solid #fbd38d', borderRadius: '10px',
                                    padding: '1rem', marginBottom: '1.2rem', fontSize: '0.82rem', color: '#6b4c11'
                                }}>
                                    <strong>Formula:</strong> Peak Charge = Base Amount × Billing Weight (minimum = Base Amount)<br />
                                    <strong>Template:</strong> if user upload the file it will apply to weight directly without multiplication (Lookup: Weight, USA, ROW)<br />
                                    <strong>Example:</strong> If set to $25 and weight is 20 KGS → $25 × 20 = $500. If weight is 0.5 KGS → $25 (minimum).
                                </div>

                                <div style={{ marginBottom: '1.2rem', padding: '1rem', background: '#f7fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                    <label style={labelSt}>📁 Upload Peak Charges Sheet (Optional)</label>
                                    <input type="file" accept=".xlsx, .xls, .csv" onChange={handleExcelPeakCharges} style={{ fontSize: '0.82rem' }} />
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '0.5rem' }}>
                                        <button onClick={handleDownloadPeakTemplate} style={{ border: 'none', background: 'none', color: '#3182ce', fontSize: '0.72rem', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>
                                            📥 Download Template
                                        </button>
                                    </div>
                                    <p style={{ fontSize: '0.73rem', color: '#718096', marginTop: '0.4rem' }}>
                                        Upload an Excel/CSV with: <strong>Weight</strong>, <strong>USA</strong>, <strong>ROW</strong> columns.
                                        This will override the base amount formula with direct lookup.
                                    </p>
                                    {globalSettings.peakChargesSheet && globalSettings.peakChargesSheet.length > 0 && (
                                        <div style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '0.75rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                                <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#4a5568' }}>
                                                    📊 Data Preview {globalSettings.peakChargesFileName ? `(${globalSettings.peakChargesFileName})` : ''}
                                                </span>
                                                <button
                                                    onClick={() => setGlobalSettings(p => ({ ...p, peakChargesSheet: [], peakChargesFileName: '' }))}
                                                    style={{
                                                        padding: '0.3rem 0.6rem', fontSize: '0.72rem', fontWeight: 700,
                                                        background: '#fed7d7', color: '#c53030', border: 'none',
                                                        borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s'
                                                    }}
                                                    onMouseOver={e => e.target.style.background = '#feb2b2'}
                                                    onMouseOut={e => e.target.style.background = '#fed7d7'}
                                                >
                                                    🗑️ Remove Sheet
                                                </button>
                                            </div>
                                            <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse', textAlign: 'left' }}>
                                                    <thead>
                                                        <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
                                                            <th style={{ padding: '0.5rem', color: '#718096' }}>Weight</th>
                                                            <th style={{ padding: '0.5rem', color: '#718096' }}>USA (USD)</th>
                                                            <th style={{ padding: '0.5rem', color: '#718096' }}>ROW (USD)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(() => {
                                                            const sheet = globalSettings.peakChargesSheet || [];
                                                            const maxRows = 6;
                                                            if (sheet.length <= maxRows) {
                                                                return sheet.map((row, i) => (
                                                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                        <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600 }}>{row.weight}</td>
                                                                        <td style={{ padding: '0.4rem 0.5rem' }}>${row.USA}</td>
                                                                        <td style={{ padding: '0.4rem 0.5rem' }}>${row.ROW}</td>
                                                                    </tr>
                                                                ));
                                                            } else {
                                                                const firstThree = sheet.slice(0, 3);
                                                                const lastThree = sheet.slice(-3);
                                                                return (
                                                                    <>
                                                                        {firstThree.map((row, i) => (
                                                                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                                <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600 }}>{row.weight}</td>
                                                                                <td style={{ padding: '0.4rem 0.5rem' }}>${row.USA}</td>
                                                                                <td style={{ padding: '0.4rem 0.5rem' }}>${row.ROW}</td>
                                                                            </tr>
                                                                        ))}
                                                                        <tr>
                                                                            <td colSpan="3" style={{ textAlign: 'center', padding: '0.3rem', color: '#a0aec0', background: '#fcfcfc' }}>
                                                                                ... {sheet.length - 6} more rows ...
                                                                            </td>
                                                                        </tr>
                                                                        {lastThree.map((row, i) => (
                                                                            <tr key={i} style={{ borderBottom: i < 2 ? '1px solid #f1f5f9' : 'none' }}>
                                                                                <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600 }}>{row.weight}</td>
                                                                                <td style={{ padding: '0.4rem 0.5rem' }}>${row.USA}</td>
                                                                                <td style={{ padding: '0.4rem 0.5rem' }}>${row.ROW}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </>
                                                                );
                                                            }
                                                        })()}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <p style={{ fontSize: '0.7rem', color: '#3182ce', fontWeight: 700, marginTop: '0.4rem' }}>
                                                ✅ Successfully parsed {globalSettings.peakChargesSheet.length} weight tiers.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div style={{ marginBottom: '1.2rem' }}>
                                    <label style={labelSt}>⭐ Peak Charges Base Amount (USD)</label>
                                    <input style={{
                                        ...inputSt,
                                        borderColor: globalSettings.peakChargesUSD && parseFloat(globalSettings.peakChargesUSD) > 0 ? '#48bb78' : '#e2e8f0',
                                        opacity: globalSettings.peakChargesSheet && globalSettings.peakChargesSheet.length > 0 ? 0.6 : 1,
                                    }}
                                        type="number" step="0.01"
                                        disabled={globalSettings.peakChargesSheet && globalSettings.peakChargesSheet.length > 0}
                                        value={globalSettings.peakChargesUSD}
                                        onChange={e => setGlobalSettings(p => ({ ...p, peakChargesUSD: e.target.value }))}
                                        placeholder="e.g. 25.00" />
                                    {globalSettings.peakChargesSheet && globalSettings.peakChargesSheet.length > 0 && (
                                        <p style={{ fontSize: '0.7rem', color: '#e53e3e', fontWeight: 600, marginTop: '2px' }}>
                                            ⚠️ Formula is disabled while Peak Sheet is active.
                                        </p>
                                    )}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                                    <div>
                                        <label style={labelSt}>⭐ Peak Period From Date</label>
                                        <input style={{ ...inputSt, borderColor: globalSettings.peakChargesFromDate ? '#48bb78' : '#e2e8f0' }}
                                            type="date"
                                            value={globalSettings.peakChargesFromDate}
                                            onChange={e => setGlobalSettings(p => ({ ...p, peakChargesFromDate: e.target.value }))} />
                                    </div>
                                    <div>
                                        <label style={labelSt}>⭐ Peak Period To Date</label>
                                        <input style={{ ...inputSt, borderColor: globalSettings.peakChargesToDate ? '#48bb78' : '#e2e8f0' }}
                                            type="date"
                                            value={globalSettings.peakChargesToDate}
                                            onChange={e => setGlobalSettings(p => ({ ...p, peakChargesToDate: e.target.value }))} />
                                    </div>
                                </div>

                                {/* Active Status */}
                                {(() => {
                                    const base = parseFloat(globalSettings.peakChargesUSD) || 0;
                                    const hasSheet = globalSettings.peakChargesSheet && globalSettings.peakChargesSheet.length > 0;
                                    const from = globalSettings.peakChargesFromDate;
                                    const to = globalSettings.peakChargesToDate;
                                    if ((base > 0 || hasSheet) && from && to) {
                                        const today = new Date();
                                        const fromD = new Date(from);
                                        const toD = new Date(to);
                                        toD.setHours(23, 59, 59);
                                        const isActive = today >= fromD && today <= toD;
                                        return (
                                            <div style={{
                                                padding: '0.6rem 1rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600,
                                                background: isActive ? '#c6f6d5' : '#e2e8f0',
                                                color: isActive ? '#276749' : '#718096'
                                            }}>
                                                {isActive ? `🔥 Peak charges are currently ACTIVE (${hasSheet ? 'Sheet lookup' : 'Base amount formula'})` : '⏸ Peak charges are not active (outside date range)'}
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                        </div>

                        {/* Special Surcharges - Full Width */}
                        <div style={card}>
                            {sectionTitle('🌍', 'Special Surcharges by Country')}

                            <div style={{
                                background: '#edf2f7', border: '1px solid #cbd5e0', borderRadius: '10px',
                                padding: '1rem', marginBottom: '1.5rem', fontSize: '0.82rem', color: '#2d3748'
                            }}>
                                <strong>Purpose:</strong> Apply country-specific surcharges during specified date ranges. These override global peak charges for the selected country only. NOT visible to companies.
                            </div>

                            {/* Add New Special Surcharge Form */}
                            <div style={{
                                background: '#f7fafc', border: '2px dashed #cbd5e0', borderRadius: '12px',
                                padding: '1.5rem', marginBottom: '1.5rem'
                            }}>
                                <div style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '1rem', color: '#2d3748' }}>
                                    ➕ Add Country-Specific Surcharge
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                    <div>
                                        <label style={labelSt}>Country Code</label>
                                        <select
                                            style={{
                                                ...inputSt,
                                                appearance: 'none',
                                                paddingRight: '2.5rem',
                                                backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27currentColor%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e")',
                                                backgroundRepeat: 'no-repeat',
                                                backgroundPosition: 'right 0.7rem center',
                                                backgroundSize: '1.5em 1.5em',
                                                cursor: 'pointer',
                                                zIndex: 100
                                            }}
                                            value={specialSurchargeForm.country}
                                            onChange={e => setSpecialSurchargeForm(p => ({ ...p, country: e.target.value }))}
                                        >
                                            <option value="">-- Select Country --</option>
                                            {countries && countries.length > 0 ? (
                                                countries.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)
                                            ) : (
                                                <option disabled>Loading countries...</option>
                                            )}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelSt}>Surcharge Amount (USD)</label>
                                        <input style={inputSt} type="number" step="0.01" placeholder="e.g. 15.00"
                                            value={specialSurchargeForm.amountUSD}
                                            onChange={e => setSpecialSurchargeForm(p => ({ ...p, amountUSD: e.target.value }))} />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                                    <div>
                                        <label style={labelSt}>Valid From Date</label>
                                        <input
                                            style={{
                                                ...inputSt,
                                                borderColor: specialSurchargeForm.fromDate && specialSurchargeForm.toDate && new Date(specialSurchargeForm.fromDate) > new Date(specialSurchargeForm.toDate) ? '#f56565' : '#e2e8f0'
                                            }}
                                            type="date"
                                            value={specialSurchargeForm.fromDate}
                                            onChange={e => setSpecialSurchargeForm(p => ({ ...p, fromDate: e.target.value }))} />
                                        <p style={{ fontSize: '0.75rem', color: '#718096', margin: '4px 0 0' }}>
                                            Today: {new Date().toISOString().split('T')[0]}
                                        </p>
                                    </div>
                                    <div>
                                        <label style={labelSt}>Valid To Date</label>
                                        <input
                                            style={{
                                                ...inputSt,
                                                borderColor: specialSurchargeForm.fromDate && specialSurchargeForm.toDate && new Date(specialSurchargeForm.fromDate) > new Date(specialSurchargeForm.toDate) ? '#f56565' : '#e2e8f0'
                                            }}
                                            type="date"
                                            value={specialSurchargeForm.toDate}
                                            onChange={e => setSpecialSurchargeForm(p => ({ ...p, toDate: e.target.value }))} />
                                        <p style={{ fontSize: '0.75rem', color: '#718096', margin: '4px 0 0' }}>
                                            Must be ≥ From Date
                                        </p>
                                    </div>
                                </div>

                                {specialSurchargeForm.fromDate && specialSurchargeForm.toDate && new Date(specialSurchargeForm.fromDate) > new Date(specialSurchargeForm.toDate) && (
                                    <div style={{
                                        background: '#fed7d7', border: '1px solid #fc8181', borderRadius: '8px',
                                        padding: '0.75rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#c53030', fontWeight: 600
                                    }}>
                                        ❌ Invalid: From Date cannot be after To Date
                                    </div>
                                )}

                                <button className="settings-btn" onClick={handleAddSpecialSurcharge}
                                    disabled={specialSurchargeForm.fromDate && specialSurchargeForm.toDate && new Date(specialSurchargeForm.fromDate) > new Date(specialSurchargeForm.toDate)}
                                    style={{ background: '#48bb78' }}>
                                    ✅ Add Surcharge
                                </button>
                            </div>

                            {/* List of Special Surcharges */}
                            {globalSettings.specialSurcharges && globalSettings.specialSurcharges.length > 0 ? (
                                <div>
                                    <div style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '1rem', color: '#2d3748' }}>
                                        📋 Active Surcharges ({globalSettings.specialSurcharges.length})
                                    </div>
                                    <div style={{
                                        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem'
                                    }}>
                                        {globalSettings.specialSurcharges.map((surcharge, idx) => {
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            const fromDay = new Date(surcharge.fromDate);
                                            const toDay = new Date(surcharge.toDate);
                                            fromDay.setHours(0, 0, 0, 0);
                                            toDay.setHours(23, 59, 59, 999);
                                            const isActive = today >= fromDay && today <= toDay;
                                            const daysRemaining = Math.ceil((toDay - today) / (1000 * 60 * 60 * 24));
                                            return (
                                                <div key={idx} style={{
                                                    background: isActive ? '#c6f6d5' : '#fef3c7',
                                                    border: isActive ? '2px solid #48bb78' : '2px solid #fbbf24',
                                                    borderRadius: '10px',
                                                    padding: '1rem'
                                                }}>
                                                    <div style={{ fontWeight: 700, color: '#2d3748', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <span>🌐 {surcharge.country}</span>
                                                        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#718096' }}>
                                                            ${surcharge.amountUSD}/kg
                                                        </span>
                                                    </div>
                                                    <div style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: '0.3rem' }}>
                                                        <strong>Period:</strong> {surcharge.fromDate} to {surcharge.toDate}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.6rem',
                                                        color: isActive ? '#276749' : '#78350f',
                                                        background: isActive ? 'rgba(72,187,120,0.1)' : 'rgba(251,191,36,0.1)',
                                                        padding: '0.4rem 0.6rem', borderRadius: '4px', textAlign: 'center'
                                                    }}>
                                                        {isActive ? `🔥 ACTIVE - ${daysRemaining} days left` : `⏳ INACTIVE - Starts ${surcharge.fromDate}`}
                                                    </div>
                                                    <button className="settings-btn" onClick={() => handleRemoveSpecialSurcharge(idx)}
                                                        style={{ width: '100%', background: '#fc8181', marginBottom: 0, fontSize: '0.8rem' }}>
                                                        🗑 Remove
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    textAlign: 'center', padding: '2rem', color: '#718096',
                                    background: '#f7fafc', borderRadius: '10px'
                                }}>
                                    <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>No special surcharges configured yet</div>
                                    <div style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>Add one above to enable country-specific pricing</div>
                                </div>
                            )}

                            {/* Verification & Save Instructions */}
                            <div style={{
                                background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px',
                                padding: '1rem', marginTop: '1.5rem'
                            }}>
                                <div style={{ fontSize: '0.85rem', color: '#166534', marginBottom: '0.8rem' }}>
                                    <strong>⚠️ IMPORTANT:</strong> Special surcharges now save immediately when you add or remove entries. Click <strong>"💾 Update Settings"</strong> only for other global settings changes.
                                </div>
                                <button className="settings-btn" onClick={handleVerifySpecialSurcharges}
                                    style={{ background: '#16a34a', width: '100%', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                                    🔍 Verify Surcharges in Database
                                </button>
                                <button className="settings-btn" onClick={handleTestSaveSurcharge}
                                    style={{ background: '#8b5cf6', width: '100%', marginBottom: 0, fontSize: '0.85rem' }}>
                                    🧪 Test Save (Check Server Logs)
                                </button>
                            </div>
                        </div>

                        {/* Zone Mapping - Full Width */}
                        <div style={card}>
                            {sectionTitle('📍', `Export Zone Mapping${zoneCount > 0 ? ` (${zoneCount} countries mapped)` : ''}`)}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', alignItems: 'end' }}>
                                <div>
                                    <label style={labelSt}>Select Zone Excel (.xlsx)</label>
                                    <input type="file" accept=".xlsx, .xls" onChange={handleExcelZones}
                                        style={{ padding: '0.5rem', background: '#e2e8f0', width: '100%', borderRadius: '6px', boxSizing: 'border-box' }} />
                                    <p style={{ fontSize: '0.75rem', color: '#718096', margin: '4px 0 0' }}>
                                        Expected format: [Code] [Country] [Zone] &nbsp;|&nbsp; {zoneRawData ? '✅ File parsed, ready to upload' : 'No file chosen'}
                                    </p>
                                </div>
                                <button className="settings-btn secondary" onClick={handleUploadZones}
                                    disabled={loading || !zoneRawData} style={{ whiteSpace: 'nowrap' }}>
                                    📤 Update Zone Map
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        background: '#fed7d7',
                        border: '2px solid #fc8181',
                        borderRadius: '14px',
                        padding: '2rem',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
                        <h3 style={{ color: '#c53030', marginBottom: '0.5rem' }}>Access Restricted</h3>
                        <p style={{ color: '#742a2a', marginBottom: '1rem' }}>
                            Global settings are restricted to super users only. These settings apply to all companies and require special permissions to modify.
                        </p>
                        <p style={{ color: '#742a2a', fontSize: '0.9rem' }}>
                            If you need to manage rates or settings for your company, contact your system administrator.
                        </p>
                    </div>
                )
            )}

            {/* ══════════════════════════════════════════════════ */}
            {/* TAB: FREIGHT TARIFFS                              */}
            {/* ══════════════════════════════════════════════════ */}
            {activeTab === 'tariffs' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

                    {/* Upload New Sheet */}
                    <div style={card}>
                        {sectionTitle('📤', 'Upload Freight Sheet')}
                        <p style={{ fontSize: '0.8rem', color: '#718096', marginBottom: '1rem' }}>
                            {companyName
                                ? `Upload rates for ${companyName}. Mandatory columns: Account, Product, Service Type, Weight.`
                                : 'Select an .xlsx file. Mandatory columns: <b>Account, Product, Service Type, Weight</b>.'
                            }
                        </p>

                        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                            <label style={labelSt}>Sheet Category</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={() => {
                                        setIsProductBoxSheet(false);
                                        setTariffData(p => ({ ...p, product: 'NON-DOCUMENTS' }));
                                    }}
                                    style={{
                                        flex: 1, padding: '0.6rem', borderRadius: '8px', border: '1.5px solid #667eea',
                                        background: !isProductBoxSheet ? '#667eea' : 'white',
                                        color: !isProductBoxSheet ? 'white' : '#667eea',
                                        fontWeight: 700, cursor: 'pointer'
                                    }}>
                                    📦 Normal Sheet
                                </button>
                                <button
                                    onClick={() => {
                                        setIsProductBoxSheet(true);
                                        setTariffData(p => ({ ...p, product: '10KG PRODUCT BOX' })); // Default for product box mode
                                    }}
                                    style={{
                                        flex: 1, padding: '0.6rem', borderRadius: '8px', border: '1.5px solid #667eea',
                                        background: isProductBoxSheet ? '#667eea' : 'white',
                                        color: isProductBoxSheet ? 'white' : '#667eea',
                                        fontWeight: 700, cursor: 'pointer'
                                    }}>
                                    🗳️ Product Box Sheet
                                </button>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
                            <button className="settings-btn" onClick={isProductBoxSheet ? handleDownloadProductBoxTemplate : handleDownloadTemplate}
                                style={{ background: '#48bb78', marginBottom: 0 }}>
                                📥 Download {isProductBoxSheet ? 'Product Box' : 'Standard'} Template
                            </button>
                            <button className="settings-btn" disabled={true}
                                style={{ opacity: 0.4, cursor: 'default', marginBottom: 0 }}>
                                {isProductBoxSheet ? '(10kg/25kg Boxes)' : '(Standard Format)'}
                            </button>
                        </div>

                        <div className="form-group">
                            <label>Select Excel File (.xlsx)</label>
                            <input type="file" accept=".xlsx, .xls" onChange={handleExcelTariff}
                                style={{ padding: '0.5rem', background: '#e2e8f0' }} />
                        </div>

                        <div className="form-group">
                            <label>Mapping Accounts (Comma Separated)</label>
                            <input placeholder="e.g. X863X3, X863X4, X863X5"
                                value={tariffData.account}
                                onChange={e => {
                                    const newAccount = e.target.value;
                                    setTariffData(p => {
                                        // Keep only comments for accounts that are still in the input
                                        const currentAccounts = newAccount.split(',').map(a => a.trim().toUpperCase()).filter(a => a);
                                        const newComments = { ...p.accountComments };
                                        Object.keys(newComments).forEach(acc => {
                                            if (!currentAccounts.includes(acc)) delete newComments[acc];
                                        });
                                        return { ...p, account: newAccount, accountComments: newComments };
                                    });
                                }} />
                            <p style={{ fontSize: '0.73rem', color: '#718096', margin: '4px 0 0' }}>
                                The uploaded sheet will be mapped to <strong>all</strong> these accounts.
                            </p>
                        </div>

                        {/* Dynamic Comment Fields per Account */}
                        {tariffData.account && tariffData.account.split(',').map(a => a.trim().toUpperCase()).filter(a => a).length > 0 && (
                            <div className="form-group" style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <label style={{ marginBottom: '10px', display: 'block', color: '#4a5568', fontWeight: 'bold' }}>💬 Account Comments</label>
                                {tariffData.account.split(',').map(a => a.trim().toUpperCase()).filter(a => a).map(acc => (
                                    <div key={acc} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ minWidth: '80px', fontWeight: '600', fontSize: '0.85rem' }}>{acc}</span>
                                        <input
                                            style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.85rem' }}
                                            placeholder={`Comment for ${acc} (Optional)`}
                                            value={tariffData.accountComments[acc] || ''}
                                            onChange={(e) => setTariffData(p => ({
                                                ...p,
                                                accountComments: { ...p.accountComments, [acc]: e.target.value }
                                            }))}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                FD Charges (USD)
                                <span style={{
                                    background: '#eef2ff', color: '#4f46e5', fontSize: '0.7rem',
                                    fontWeight: 700, padding: '2px 8px', borderRadius: '999px', letterSpacing: '0.5px'
                                }}>DDP Only</span>
                            </label>
                            <input
                                type="number" step="0.01" min="0"
                                placeholder="Default: 21.50"
                                value={tariffData.fdCharges}
                                onChange={e => setTariffData(p => ({ ...p, fdCharges: e.target.value }))} />
                            <p style={{ fontSize: '0.73rem', color: '#718096', margin: '4px 0 0' }}>
                                Applied to DDP shipments for this account. Default is <strong>21.50 USD</strong> if not set.
                            </p>
                        </div>

                        <button className="settings-btn" onClick={handleUploadTariff}
                            disabled={loading || !tariffData.rawData}>
                            {loading ? '⏳ Processing...' : '💾 Save Freight Sheet'}
                        </button>
                    </div>

                    {/* Stored Sheets */}
                    <div style={card}>
                        {sectionTitle('📋', 'Stored Freight Sheets')}

                        {existingTariffs.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#a0aec0' }}>
                                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📂</div>
                                <p>No sheets uploaded yet.</p>
                            </div>
                        ) : (
                            <div className="tariff-list">
                                {existingTariffs.map((t, i) => (
                                    <div key={i} className="tariff-item">
                                        <div className="tariff-info">
                                            <span className="tariff-id">{t.account}</span>
                                            <span className="tariff-meta">{t.serviceType} ({t.count} tiers)</span>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                background: '#eef2ff', color: '#4f46e5',
                                                fontSize: '0.68rem', fontWeight: 700,
                                                padding: '2px 8px', borderRadius: '999px',
                                                marginLeft: '6px'
                                            }}>
                                                💰 FD: ${t.fdCharges !== undefined ? Number(t.fdCharges).toFixed(2) : '21.50'}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <span className="status-badge success">Active</span>
                                            <button
                                                style={{ background: '#fed7d7', color: '#c53030', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '0.7rem' }}
                                                onClick={() => handleDeleteSheet(t.account, t.serviceType)}>
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════ */}
            {/* TAB: IMPORT TARIFFS                               */}
            {/* ══════════════════════════════════════════════════ */}
            {activeTab === 'import-tariffs' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

                    {/* Upload New Import Sheet */}
                    <div style={card}>
                        {sectionTitle('✈️', 'Upload Import Tariff Sheet')}
                        <div style={{
                            background: 'linear-gradient(135deg, #e0f2fe, #bae6fd)',
                            border: '1px solid #7dd3fc', borderRadius: '10px',
                            padding: '0.9rem 1rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#0369a1'
                        }}>
                            ℹ️ Import tariff accounts are <strong>exclusively</strong> visible in FC Label &amp; Request FC modules.
                            Rate lookup is based on the <strong>origin country</strong> (where the parcel ships FROM).
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
                            <button className="settings-btn" onClick={handleDownloadImportTemplate}
                                style={{ background: '#0284c7', marginBottom: 0 }}>
                                📥 Download Template
                            </button>
                            <button className="settings-btn" disabled={true}
                                style={{ opacity: 0.4, cursor: 'default', marginBottom: 0 }}>
                                (Import Saver Format)
                            </button>
                        </div>

                        <div className="form-group">
                            <label>Select Excel File (.xlsx)</label>
                            <input type="file" accept=".xlsx, .xls" onChange={handleExcelImportTariff}
                                style={{ padding: '0.5rem', background: '#e2e8f0' }} />
                        </div>

                        <div className="form-group">
                            <label>Confirm Account Number</label>
                            <input placeholder="e.g. K0F754"
                                value={importTariffData.account}
                                onChange={e => setImportTariffData(p => ({ ...p, account: e.target.value }))} />
                        </div>

                        <div className="form-group">
                            <label>Product Type</label>
                            <select value={importTariffData.product}
                                onChange={e => setImportTariffData(p => ({ ...p, product: e.target.value }))}>
                                <option value="NON-DOCUMENTS">NON-DOCUMENTS</option>
                                <option value="DOCUMENTS">DOCUMENTS</option>
                                <option value="LETTERS">LETTERS</option>
                            </select>
                        </div>

                        <button className="settings-btn" onClick={handleUploadImportTariff}
                            disabled={loading || !importTariffData.rawData}
                            style={{ background: '#0369a1' }}>
                            {loading ? '⏳ Processing...' : '💾 Save Import Sheet'}
                        </button>
                    </div>

                    {/* Stored Import Sheets */}
                    <div style={card}>
                        {sectionTitle('📋', `Stored Import Sheets (${existingImportTariffs.length})`)}

                        {existingImportTariffs.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#a0aec0' }}>
                                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✈️</div>
                                <p>No import sheets uploaded yet.</p>
                                <p style={{ fontSize: '0.78rem' }}>Download the template, fill with Import Saver rates, then upload above.</p>
                            </div>
                        ) : (
                            <div className="tariff-list">
                                {existingImportTariffs.map((t, i) => (
                                    <div key={i} className="tariff-item">
                                        <div className="tariff-info">
                                            <span className="tariff-id">{t.account}</span>
                                            <span className="tariff-meta">{t.serviceType} ({t.count} tiers)</span>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                background: '#e0f2fe', color: '#0369a1',
                                                fontSize: '0.68rem', fontWeight: 700,
                                                padding: '2px 8px', borderRadius: '999px', marginLeft: '6px'
                                            }}>✈️ Import</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <span className="status-badge success">Active</span>
                                            <button
                                                style={{ background: '#fed7d7', color: '#c53030', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '0.7rem' }}
                                                onClick={() => handleDeleteImportSheet(t.account, t.serviceType)}>
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════ */}
            {/* TAB: RECEIPT MANAGEMENT                           */}
            {/* ══════════════════════════════════════════════════ */}
            {activeTab === 'receipts' && (
                <div>
                    <div style={{ ...card }}>
                        {sectionTitle('🧾', 'Receipt & Invoice Management')}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2rem' }}>

                            {/* Reset Counter */}
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.5rem' }}>Set Next Receipt Number</div>
                                <p style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '1rem' }}>
                                    Enter the exact number you want the next receipt to start from (e.g. 8200000).
                                </p>
                                <button className="settings-btn" onClick={handleOpenReceiptResetModal}
                                    style={{ background: '#4a5568', padding: '0.75rem 1.5rem', width: '100%' }}>
                                    🔐 Reset Receipt Number
                                </button>
                            </div>

                            {/* Delete Receipt */}
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.5rem' }}>Delete Shipment Receipt</div>
                                <p style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '1rem' }}>
                                    Remove the receipt number assigned to a tracking number.
                                </p>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <input type="text" placeholder="Tracking Number" id="deleteReceiptTracking"
                                        style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                                    <button className="settings-btn" style={{ background: '#c53030', padding: '0.5rem 1rem' }}
                                        onClick={async () => {
                                            const trk = document.getElementById('deleteReceiptTracking').value;
                                            if (!trk) return alert('Enter tracking number');
                                            if (!window.confirm(`Delete receipt for ${trk}?`)) return;
                                            try {
                                                await axios.delete(API(`/shipping/receipt/${trk}`));
                                                alert('Receipt Deleted from Shipment');
                                            } catch (e) { alert('Delete Failed'); }
                                        }}>
                                        Delete
                                    </button>
                                </div>
                            </div>

                            {/* Assign Receipt */}
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.5rem' }}>Assign Manual Receipt No.</div>
                                <p style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '1rem' }}>
                                    Link a specific tracking number to a custom receipt number.
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <input type="text" placeholder="Tracking Number" id="assignTrk"
                                        style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                                    <input type="text" placeholder="Custom Receipt No." id="assignNo"
                                        style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                                    <button className="settings-btn" style={{ background: '#2b6cb0' }}
                                        onClick={async () => {
                                            const trk = document.getElementById('assignTrk').value;
                                            const no = document.getElementById('assignNo').value;
                                            if (!trk || !no) return alert('Enter both fields');
                                            try {
                                                await axios.post(API('/shipping/receipt/assign'), { trackingNumber: trk, receiptNo: no });
                                                alert('Receipt Assigned and Saved');
                                            } catch (e) { alert('Assignment Failed'); }
                                        }}>
                                        Assign & Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Receipt Reset Modal ──────────────────────────────────────────── */}
            {showReceiptResetModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        background: 'white', borderRadius: '16px', padding: '2rem',
                        maxWidth: '500px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: '#1a202c' }}>🔐 Reset Receipt Counter</h3>
                            <button onClick={handleCloseReceiptResetModal}
                                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' }}>×</button>
                        </div>

                        {receiptResetStep === 1 ? (
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 700, color: '#2d3748' }}>
                                    Enter New Starting Receipt Number
                                </label>
                                <p style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '1rem' }}>
                                    Enter the exact number you want the next receipt to start from (e.g. 8200000).
                                </p>
                                <input type="number" placeholder="e.g. 8200000"
                                    value={receiptResetNumber}
                                    onChange={e => setReceiptResetNumber(e.target.value)}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '2px solid #cbd5e0', fontSize: '1rem', marginBottom: '1.5rem', boxSizing: 'border-box' }} />
                                <div style={{ display: 'flex', gap: '0.75rem' }}>
                                    <button onClick={handleSendReceiptResetCode}
                                        disabled={receiptResetLoading || !receiptResetNumber}
                                        style={{
                                            flex: 1, padding: '0.75rem', background: receiptResetLoading || !receiptResetNumber ? '#a0aec0' : '#4a5568',
                                            color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700,
                                            cursor: receiptResetLoading || !receiptResetNumber ? 'default' : 'pointer'
                                        }}>
                                        {receiptResetLoading ? '⏳ Sending...' : '📧 Send Verification Code'}
                                    </button>
                                    <button onClick={handleCloseReceiptResetModal}
                                        style={{ flex: 0.5, padding: '0.75rem', background: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div style={{ background: '#f7fafc', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#4a5568' }}>
                                        <strong>New Receipt Number:</strong> {receiptResetNumber}
                                    </p>
                                </div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 700, color: '#2d3748' }}>
                                    Enter Verification Code
                                </label>
                                <p style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '1rem' }}>Check your email for the verification code.</p>
                                <input type="text" placeholder="Enter 6-digit code from email"
                                    value={receiptResetPin}
                                    onChange={e => setReceiptResetPin(e.target.value)}
                                    maxLength="10"
                                    style={{
                                        width: '100%', padding: '0.75rem', borderRadius: '8px', border: '2px solid #cbd5e0',
                                        fontSize: '1.2rem', textAlign: 'center', letterSpacing: '3px',
                                        marginBottom: '1.5rem', boxSizing: 'border-box'
                                    }} />
                                <div style={{ display: 'flex', gap: '0.75rem' }}>
                                    <button onClick={handleConfirmReceiptReset}
                                        disabled={receiptResetLoading || !receiptResetPin}
                                        style={{
                                            flex: 1, padding: '0.75rem', background: receiptResetLoading || !receiptResetPin ? '#a0aec0' : '#38a169',
                                            color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700,
                                            cursor: receiptResetLoading || !receiptResetPin ? 'default' : 'pointer'
                                        }}>
                                        {receiptResetLoading ? '⏳ Verifying...' : '✅ Confirm Reset'}
                                    </button>
                                    <button onClick={() => setReceiptResetStep(1)} disabled={receiptResetLoading}
                                        style={{ flex: 0.5, padding: '0.75rem', background: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>
                                        Back
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
